//codex-- Keep the toolbar redesign scoped to pages in this folder.
import PageToolbar, { PageToolbarSearch as SearchBarCP } from './PageToolbar';
import React, { useState, useEffect } from "react";
import axios from "axios";
import HeadingCp from "../components/HeadingCp";
import LoadingSpinner from "../components/LoadingSpinner";
import Modal from "../components/Modal";
import { toast } from "react-toastify";
import Truncate from "../components/Truncate";
import ModalWindow from "../components/ModalWindow";

import { useNavigate } from "react-router-dom";
import SelectCustomerMaintenance from "../components/SelectCustomerMaintenance";

const initialForm = {
  customer_id: '',
  contract_type: '',
  job_site_address: '',
  job_site_address2: '',
  job_site_city: '',
  job_site_state: '',
  job_site_zip: '',
  notes: '',
  system_type: '',
  status: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
  hold: 0,
};

const ServiceJobsites = () => {
  const navigate = useNavigate();
  const [jobsites, setJobsites] = useState([]);
  const [filteredJobsites, setFilteredJobsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [notesIdOpen, setNotesIdOpen] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState(null); // For SelectCustomerMaintenance

  // Modals & form state
  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState(initialForm);

  // For customer filter/search (if you want a dropdown)
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchJobsites();
    fetchCustomers();
  }, []);

  const fetchJobsites = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/service_crud.php?getJobsites=1`);
      const data = Array.isArray(res.data[0]) ? res.data[0] : [];
      setIsAdmin(res.data[1] || false);
      setJobsites(data);
      setFilteredJobsites(data);
    } catch (error) {
      toast.error("Error fetching jobsites");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${apiUrl}/customer_crud.php?getList=1`);
      setCustomers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      // Optional: handle customer load error
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    if (!value) {
      setFilteredJobsites(jobsites);
      return;
    }
    const lower = value.toLowerCase();
    setFilteredJobsites(
      jobsites.filter(js =>
        (js.job_site_address && js.job_site_address.toLowerCase().includes(lower)) ||
        (js.job_site_city && js.job_site_city.toLowerCase().includes(lower)) ||
        (js.job_site_state && js.job_site_state.toLowerCase().includes(lower)) ||
        (js.system_type && js.system_type.toLowerCase().includes(lower)) ||
        (js.notes && js.notes.toLowerCase().includes(lower))
      )
    );
  };

  const handleSearchClick = async () => {
    setLoading(true);
    const bodyData = {
      status: 'searchJobSites',
      data: { search: searchTerm }
    };
    try {
      const response = await axios.post(
        `${apiUrl}/service_crud.php`,
        bodyData,
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data && Array.isArray(response.data)) {
        setFilteredJobsites(response.data);
      } else {
        setFilteredJobsites([]);
        toast.error('Not found: ' + searchTerm, { position: 'top-center' });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please login again.", {
          position: "top-center"
        });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: "top-center"
        });
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleCustomerFilter = (e) => {
    setSelectedCustomer(e.target.value);
    if (!e.target.value) {
      setFilteredJobsites(jobsites);
      return;
    }
    setFilteredJobsites(jobsites.filter(js => String(js.customer_id) === e.target.value));
  };

  // ---------- Create ----------
  const handleShowCreate = () => {
    setEditingRow(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  // ---------- Edit ----------
  const handleEdit = (js) => {
    setEditingRow(js.id);
    setFormData({
      ...js,
      job_site_zip: js.job_site_zip ?? '',
      hold: js.hold ?? 0,
      status: js.status ?? '',
    });
    setShowModal(true);
  };

  // ---------- Change ----------
  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ---------- Save (Create or Edit) ----------
  const handleSubmit = async () => {
    // Validation (example)
    if (!formData.customer_id) {
      toast.error("Customer is required");
      return;
    }
    if (!formData.job_site_address) {
      toast.error("Job Site Address is required");
      return;
    }
    if (!formData.job_site_city) {
      toast.error("City is required");
      return;
    }
    if (!formData.job_site_state) {
      toast.error("State is required");
      return;
    }

    setLoading(true);
    try {
      if (editingRow) {
        // Update
        const response = await axios.post(`${apiUrl}/service_crud.php`, {
          status: "update_jobsite",
          data: { ...formData, id: editingRow }
        });
        toast.success("Updated!");
      } else {
        // Create
        const response = await axios.post(`${apiUrl}/service_crud.php`, {
          status: "create_jobsite",
          data: formData,
        });
        toast.success("Created!");
      }
      setShowModal(false);
      setFormData(initialForm);
      setEditingRow(null);
      fetchJobsites();
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Modal Close ----------
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRow(null);
    setFormData(initialForm);
  };

  const handleCompanySelect = (item) => {
    if (!item) {
      // Clear customer selection safely
      setSelectedCompany(null);
      setFormData(prev => ({
        ...prev,
        customer_id: '',
        customer_name: ''
      }));
      return;
    }  
    setSelectedCompany(item);
    setFormData(prev => ({
      ...prev,
      customer_id: item.customer_id,
      customer_name: item.customer_name
    }));
  };
  

  const handleCustomerChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      customer_name: value,
      customer_id: value ? prev.customer_id : '',
    }));
    if (!value) {
      setSelectedCompany(null); // triggers search UI
    }
  };
  
  

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
      <HeadingCp label="Service Job Sites" />
        <>{/*codex-- Match the updated toolbar: grouped actions, labeled controls, and lighter original colors. */}
          <PageToolbar title="Job site tools" actions={<>
            <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm" onClick={handleShowCreate}>+ Add Job Site</button>
          </>}>
            <SearchBarCP search={searchTerm} handleChange={e => setSearchTerm(e.target.value)} onClick={handleSearchClick} />
          </PageToolbar>
      </>

      {loading ? (
        <LoadingSpinner>Loading...</LoadingSpinner>
      ) : (
        <div className="rounded-lg border shadow-sm">
        <table className="overflow-hidden min-w-full bg-gray-50 text-xs sm:text-sm border-separate border-spacing-0">
          <thead>
            <tr className="bg-gray-200 text-gray-700 uppercase text-xs">
              <th className="px-3 py-3 text-left font-bold rounded-tl-lg">ID</th>
              <th className="px-3 py-3 text-left font-bold">Type /Customer</th>
              <th className="px-3 py-3 text-left font-bold">Site &amp; Address</th>
              <th className="px-3 py-3 text-left font-bold">System</th>
              <th className="px-3 py-3 text-left font-bold">Contact Info</th>
              <th className="px-3 py-3 text-left font-bold">Hold</th>
              <th className="px-3 py-3 text-left font-bold">Notes</th>
            </tr>
          </thead>
          <tbody>
            {filteredJobsites.map((js, idx) => (
              <tr
                key={js.id}
                className={`transition group hover:bg-blue-50 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
              >
                <td className="px-3 py-2 text-gray-500">
                  <button
                    className="bg-blue-600 group-hover:bg-blue-700 transition text-white px-3 py-1 rounded shadow-sm hover:shadow active:scale-95"
                    onClick={() => handleEdit(js)}
                  >
                  {js.id}
                  </button>                  
                </td>
                <td className="px-3 py-2 text-gray-800">
                  <p className="text-gray-400 text-xs">{js.contract_type}</p>
                  {js.customer_name}
                </td>
                <td className="px-3 py-2 text-gray-900 min-w-[180px]">
                  <div className="font-medium">{js.job_site_address}</div>
                  {js.job_site_address2 && <div className="text-gray-500">{js.job_site_address2}</div>}
                  <div className="text-gray-500 text-xs">
                    {js.job_site_city}, {js.job_site_state} {js.job_site_zip}
                  </div>
                </td>
                <td className="px-3 py-2 text-gray-700">{js.system_type}</td>

                <td className="px-3 py-2 text-gray-700 max-w-xs break-words">
                  {js.contact_name && <div className="font-medium">{js.contact_name}</div>}
                  {js.contact_phone && <div className="text-gray-500">{js.contact_phone}</div>}
                  {js.contact_email && <div className="text-gray-400">{js.contact_email}</div>}
                </td>
                <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                  {/* {js.status === "1" || js.status === 1 ? (
                    <span className="px-2 py-1 rounded bg-green-100 text-green-700 font-semibold">Active</span>
                  ) : js.status === "0" || js.status === 0 ? (
                    <span className="px-2 py-1 rounded bg-gray-200 text-gray-500">Inactive</span>
                  ) : (
                    <span className="px-2 py-1 rounded bg-yellow-100 text-yellow-700">{js.status}</span>
                  )}
                  <br /> */}
                  {js.hold === 1 ? (
                    <span className="mt-1 inline-block px-2 py-1 rounded bg-red-100 text-red-600 font-semibold">Hold</span>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-gray-600 max-w-[160px]" title={js.notes}>
                  {/* {js.notes} */}
                  {js.notes && 
                    <Truncate text={js.notes} handleNotesView={() => setNotesIdOpen(js.id)} />
                  }
                  {notesIdOpen === js.id && (
                    <ModalWindow job={js.notes} closeNotes={() => setNotesIdOpen(null)} activeButton={true} />
                  )}                  
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>      
      )}

      {/* CREATE/EDIT MODAL */}
      <Modal isOpen={showModal} onClose={handleCloseModal}>
        <div className="border-b border-gray-900/10 pb-8 mb-4">
          <h2 className="text-lg font-semibold text-gray-700 mb-2">
            {editingRow ? 'Edit' : 'Add'} Job Site
          </h2>
          <label className="flex items-center space-x-2 mb-2">
            <input
              type="checkbox"
              name="hold"
              checked={Boolean(formData.hold)}
              onChange={e => setFormData(prev => ({
                ...prev,
                hold: e.target.checked ? 1 : 0
              }))}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <span className="text-red-600 font-bold">On Hold</span>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            {/* Customer Selection/Editing */}
            <div className="sm:col-span-2">
              {formData.customer_name ? (
                <div>
                  <label htmlFor="customer_name" className="block text-sm font-medium text-gray-900">
                    Customer Name <span className="text-red-500">*</span>
                    <span className="text-xs text-gray-500 ml-1">| Clear to search/select</span>
                  </label>
                  <input
                    type="text"
                    id="customer_name"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleCustomerChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    required
                  />
                </div>
              ) : (
                <SelectCustomerMaintenance
                  label="Select Customer Name"
                  items={customers}
                  selectedItem={selectedCompany}
                  setSelectedItem={handleCompanySelect}
                  apiUrl={`${apiUrl}/contracts_crud.php`}
                />
              )}
            </div>

            {/* Site Type */}
            <div>
              <label className="block text-sm font-medium text-gray-900 py-1">Site Type</label>
              <select
                name="contract_type"
                value={formData.contract_type}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              >
                <option value="">Select Site Type</option>
                <option value="Base Building">Base Building</option>
                <option value="Tenant">Tenant</option>
              </select>
            </div>

            {/* System Type */}
            <div>
              <label className="block text-sm font-medium text-gray-900 py-1">System Type</label>
              <input name="system_type" value={formData.system_type} onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
            </div>

            {/* Job Site Address */}
            <div>
              <label className="block text-sm font-medium text-gray-900 py-1">Job Site Address</label>
              <input name="job_site_address" value={formData.job_site_address} onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
            </div>

            {/* Address 2 */}
            <div>
              <label className="block text-sm font-medium text-gray-900 py-1">Address 2</label>
              <input name="job_site_address2" value={formData.job_site_address2} onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
            </div>

            {/* City / State / Zip - 60/20/20 row */}
            <div className="col-span-2 flex gap-2">
              <label className="w-[60%] min-w-0">
                <span className="block text-sm font-medium text-gray-900 py-1">City</span>
                <input
                  name="job_site_city"
                  value={formData.job_site_city}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </label>
              <label className="w-[20%] min-w-0">
                <span className="block text-sm font-medium text-gray-900 py-1">State</span>
                <input
                  name="job_site_state"
                  value={formData.job_site_state}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </label>
              <label className="w-[20%] min-w-0">
                <span className="block text-sm font-medium text-gray-900 py-1">Zip</span>
                <input
                  type="number"
                  name="job_site_zip"
                  value={formData.job_site_zip}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </label>
            </div>

            {/* Contact Name */}
            <div>
              <label className="block text-sm font-medium text-gray-900 py-1">Contact Name</label>
              <input name="contact_name" value={formData.contact_name} onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
            </div>

            {/* Contact Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-900 py-1">Contact Phone</label>
              <input type="number" name="contact_phone" value={formData.contact_phone} onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
            </div>

            {/* Contact Email */}
            <div>
              <label className="block text-sm font-medium text-gray-900 py-1">Contact Email</label>
              <input name="contact_email" value={formData.contact_email} onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
            </div>

            {/* Notes */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-900 py-1">Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" rows={2} />
            </div>
          </div>

          <div className="flex gap-2 mt-8 justify-end">
          {isAdmin && (
              <button
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded shadow"
                onClick={async () => {
                  // if (window.confirm("Are you sure you want to delete this job site?")) {
                    try {
                      setLoading(true);
                      await axios.post(`${apiUrl}/service_crud.php`, {
                        status: "delete_jobsite",
                        data: { id: editingRow }
                      });
                      toast.success("Job site deleted!");
                      setShowModal(false);
                      setFormData(initialForm);
                      setEditingRow(null);
                      fetchJobsites();
                    } catch (error) {
                      toast.error("Failed to delete job site");
                    } finally {
                      setLoading(false);
                    }
                  // }
                }}
              >
                Delete
              </button>
            )}            
            <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded shadow" onClick={handleSubmit}>
              Save
            </button>
            <button className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded" onClick={handleCloseModal}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default ServiceJobsites;
